import React, { useState, useRef, useEffect } from "react";
import { CollisionRule, HandicapSettings, RankingMode } from "../types.ts";
import {
  COLLISION_RULE_LABELS,
  formatTime,
  parseTime,
} from "../utils/tournament-calc.ts";
import { RANKING_MODES } from "../utils/ranking.ts";
import { deleteAllResults, getAllResults } from "../utils/firebase-utils.js";

interface ConfigurationProps {
  playerCount: number;
  setPlayerCount: (count: number) => void;
  exportData: () => void;
  importData: (jsonData: string) => void;
  handicapSettings: HandicapSettings;
  setHandicapSettings: (settings: HandicapSettings) => void;
  rankingMode: RankingMode;
  setRankingMode: (mode: RankingMode) => void;
}

const Configuration: React.FC<ConfigurationProps> = ({
  playerCount,
  setPlayerCount,
  exportData,
  importData,
  handicapSettings,
  setHandicapSettings,
  rankingMode,
  setRankingMode,
}) => {
  const [theme, setTheme] = useState<string>("light");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Rozepsané hodnoty mezí – zapíší se až po opuštění pole
  const [minDraft, setMinDraft] = useState<string>(
    formatTime(handicapSettings.min)
  );
  const [maxDraft, setMaxDraft] = useState<string>(
    formatTime(handicapSettings.max)
  );
  const [handicapError, setHandicapError] = useState<string>("");
  const [deletingResults, setDeletingResults] = useState<boolean>(false);

  useEffect(() => {
    setMinDraft(formatTime(handicapSettings.min));
    setMaxDraft(formatTime(handicapSettings.max));
  }, [handicapSettings.min, handicapSettings.max]);

  const commitMin = () => {
    const parsed = parseTime(minDraft);
    if (parsed === null) {
      setHandicapError("Minimální čas musí být ve tvaru mm:ss (např. 1:30).");
      setMinDraft(formatTime(handicapSettings.min));
      return;
    }
    if (parsed >= handicapSettings.max) {
      setHandicapError("Minimální čas musí být menší než maximální.");
      setMinDraft(formatTime(handicapSettings.min));
      return;
    }
    setHandicapError("");
    setHandicapSettings({ ...handicapSettings, min: parsed });
  };

  const commitMax = () => {
    const parsed = parseTime(maxDraft);
    if (parsed === null) {
      setHandicapError("Maximální čas musí být ve tvaru mm:ss (např. 10:00).");
      setMaxDraft(formatTime(handicapSettings.max));
      return;
    }
    if (parsed <= handicapSettings.min) {
      setHandicapError("Maximální čas musí být větší než minimální.");
      setMaxDraft(formatTime(handicapSettings.max));
      return;
    }
    setHandicapError("");
    setHandicapSettings({ ...handicapSettings, max: parsed });
  };

  // Stav pro automatické odesílání emailů
  const [autoSendResults, setAutoSendResults] = useState<boolean>(false);
  const [emailAddresses, setEmailAddresses] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");

  // Načtení nastavení emailů z localStorage
  useEffect(() => {
    const savedAutoSend = localStorage.getItem("autoSendResults");
    if (savedAutoSend === "true") {
      setAutoSendResults(true);
    }

    const savedEmails = localStorage.getItem("emailRecipients");
    if (savedEmails) {
      try {
        setEmailAddresses(JSON.parse(savedEmails));
      } catch (e) {
        console.error("Chyba při načítání emailových adres:", e);
      }
    }
  }, []);

  // Uložení nastavení emailů do localStorage
  useEffect(() => {
    localStorage.setItem("autoSendResults", autoSendResults.toString());
    localStorage.setItem("emailRecipients", JSON.stringify(emailAddresses));
  }, [autoSendResults, emailAddresses]);

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === "string") {
        importData(content);
      }
    };
    reader.readAsText(file);

    // Reset the file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Smaže lokální data turnajů (rozehranou tabulku, sestavu a lokální archiv).
   * Hráči (allPlayers) a veškeré nastavení zůstávají.
   */
  const handleClearTournaments = () => {
    if (
      window.confirm(
        "Smazat lokální data turnajů? Zůstanou hráči i nastavení. " +
          "Turnajů uložených ve Firebase se to netýká."
      )
    ) {
      ["saved_tournaments", "results", "players"].forEach((key) =>
        localStorage.removeItem(key)
      );
      window.location.reload();
    }
  };

  /**
   * Smaže všechny turnaje z Firebase (kolekce "results").
   * Nevratné – proto se nejdřív stáhne záloha a potvrzuje se dvakrát.
   * Hráčů se to netýká.
   */
  const handleDeleteAllResults = async () => {
    if (
      !window.confirm(
        "Smazat VŠECHNY turnaje z databáze (Firebase)?\n\n" +
          "Nejdřív se stáhne záloha do souboru JSON a pak se zeptám ještě " +
          "jednou. Hráči zůstanou nedotčeni."
      )
    ) {
      return;
    }

    setDeletingResults(true);
    try {
      const all = await getAllResults();

      if (!all || all.length === 0) {
        alert("V databázi nejsou žádné turnaje ke smazání.");
        return;
      }

      // Záloha před smazáním
      const blob = new Blob([JSON.stringify(all, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `turnaje-zaloha-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (
        !window.confirm(
          `Záloha ${all.length} turnajů byla stažena.\n\n` +
            "Opravdu je všechny smazat z databáze? Tuto akci nelze vrátit zpět."
        )
      ) {
        return;
      }

      const deleted = await deleteAllResults();
      alert(
        `Smazáno ${deleted} turnajů. Záložka "Výsledky turnajů" bude prázdná.`
      );
    } catch (err) {
      console.error("Chyba při mazání turnajů:", err);
      alert(
        "Turnaje se nepodařilo smazat. Zkontrolujte připojení a oprávnění " +
          "ve Firebase."
      );
    } finally {
      setDeletingResults(false);
    }
  };

  const handleClearData = () => {
    if (
      window.confirm(
        "Opravdu chcete smazat všechna data? Tato akce je nevratná."
      )
    ) {
      localStorage.clear();
      window.location.reload();
    }
  };

  // Validace emailu
  const validateEmail = (email: string): boolean => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  // Přidání nového emailu
  const addEmail = () => {
    if (!newEmail.trim()) {
      setEmailError("Email nemůže být prázdný");
      return;
    }

    if (!validateEmail(newEmail)) {
      setEmailError("Neplatný formát emailu");
      return;
    }

    if (emailAddresses.includes(newEmail.trim())) {
      setEmailError("Tento email již existuje v seznamu");
      return;
    }

    setEmailAddresses([...emailAddresses, newEmail.trim()]);
    setNewEmail("");
    setEmailError("");
  };

  // Odebrání emailu
  const removeEmail = (index: number) => {
    const newEmails = [...emailAddresses];
    newEmails.splice(index, 1);
    setEmailAddresses(newEmails);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-6">Konfigurace</h2>

      <div className="space-y-8">
        {/* Tournament settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Nastavení turnaje
          </h3>
          <div className="mt-2">
            <label
              htmlFor="playerCount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Počet hráčů v turnaji:
            </label>
            <select
              id="playerCount"
              value={playerCount}
              onChange={(e) => setPlayerCount(parseInt(e.target.value))}
              className="mt-1 block w-64 pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {[...Array(13).keys()]
                .map((i) => i + 4)
                .map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Změna počtu hráčů zachová existující data a výsledky v tabulce
              turnaje.
            </p>
          </div>
        </div>

        {/* Ranking settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Hodnocení pořadí
          </h3>
          <div className="space-y-3">
            {RANKING_MODES.map((mode) => (
              <label
                key={mode.id}
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${
                  rankingMode === mode.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="rankingMode"
                  value={mode.id}
                  checked={rankingMode === mode.id}
                  onChange={() => setRankingMode(mode.id)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    {mode.label}
                  </span>
                  <span className="block text-sm text-gray-500">
                    {mode.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Nastavení se promítne do aktuálního turnaje. Už uložené turnaje si
            navždy drží pořadí i režim, se kterým byly uzavřeny.
          </p>
        </div>

        {/* Handicap settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Přepočet časů po turnaji
          </h3>

          <div className="flex flex-wrap gap-6">
            <div>
              <label
                htmlFor="handicapMin"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Minimální čas [mm:ss]
              </label>
              <input
                id="handicapMin"
                type="text"
                value={minDraft}
                onChange={(e) => setMinDraft(e.target.value)}
                onBlur={commitMin}
                onKeyDown={(e) => e.key === "Enter" && commitMin()}
                className="mt-1 block w-32 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="handicapMax"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Maximální čas [mm:ss]
              </label>
              <input
                id="handicapMax"
                type="text"
                value={maxDraft}
                onChange={(e) => setMaxDraft(e.target.value)}
                onBlur={commitMax}
                onKeyDown={(e) => e.key === "Enter" && commitMax()}
                className="mt-1 block w-32 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {handicapError && (
            <p className="mt-2 text-sm text-red-600">{handicapError}</p>
          )}

          <div className="mt-4">
            <label
              htmlFor="collisionRule"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Když na hráče sedí bonus i přirážka zároveň
            </label>
            <select
              id="collisionRule"
              value={handicapSettings.collisionRule}
              onChange={(e) =>
                setHandicapSettings({
                  ...handicapSettings,
                  collisionRule: e.target.value as CollisionRule,
                })
              }
              className="mt-1 block w-full max-w-md pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {(
                Object.keys(COLLISION_RULE_LABELS) as CollisionRule[]
              ).map((rule) => (
                <option key={rule} value={rule}>
                  {COLLISION_RULE_LABELS[rule]}
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Po turnaji si 1. místo odečte 2 minuty, 2. a 3. místo 1 minutu a
              poslední tři místa si 1 minutu přičtou. Nový čas se vždy drží v
              rozmezí {formatTime(handicapSettings.min)} až{" "}
              {formatTime(handicapSettings.max)}. Pod minimum přepočet nikdy
              nikoho nepošle – kratší čas je dobrovolný a nastavit si ho může
              jen sám hráč ručně v tabulce turnaje. Komu bonus vyjde pod
              minimum, zůstane na svém čase; nikdy se mu neprodlouží. V
              turnaji do pěti hráčů sedí na jednoho hráče obě pravidla
              zároveň – tehdy se uplatní to zvolené níže.
            </p>
          </div>
        </div>

        {/* Email settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Nastavení emailů
          </h3>
          <div className="mb-4">
            <div className="flex items-center">
              <input
                id="autoSendResults"
                type="checkbox"
                checked={autoSendResults}
                onChange={(e) => setAutoSendResults(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor="autoSendResults"
                className="ml-2 block text-sm text-gray-700"
              >
                Automaticky odeslat výsledky na email po dokončení turnaje
              </label>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Emailové adresy pro zasílání výsledků
            </label>

            <div className="space-y-2">
              {emailAddresses.map((email, index) => (
                <div
                  key={index}
                  className="flex items-center p-2 bg-gray-50 rounded-md"
                >
                  <span className="flex-grow">{email}</span>
                  <button
                    onClick={() => removeEmail(index)}
                    className="text-red-600 hover:text-red-800 ml-2"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-3 flex space-x-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addEmail()}
                placeholder="Zadejte nový email"
                className="flex-grow mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <button
                onClick={addEmail}
                className="mt-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Přidat
              </button>
            </div>
            {emailError && (
              <p className="mt-1 text-sm text-red-600">{emailError}</p>
            )}

            <p className="mt-2 text-sm text-gray-500">
              Výsledky turnaje budou automaticky odeslány na tyto emailové
              adresy.
            </p>
          </div>
        </div>

        {/* Theme settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Téma aplikace
          </h3>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setTheme("light")}
              className={`px-4 py-2 rounded-md border ${
                theme === "light"
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Světlé
            </button>
            <button
              onClick={() => setTheme("dark")}
              className={`px-4 py-2 rounded-md border ${
                theme === "dark"
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Tmavé
            </button>
          </div>
        </div>

        {/* Data management */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Správa dat</h3>
          <div className="flex items-center space-x-4">
            <button
              onClick={exportData}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Exportovat data
            </button>
            <button
              onClick={handleImportClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Importovat data
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".json"
              className="hidden"
            />
            <button
              onClick={handleClearTournaments}
              className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
            >
              Vymazat turnaje
            </button>
            <button
              onClick={handleClearData}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Vymazat všechna data
            </button>
            <button
              onClick={handleDeleteAllResults}
              disabled={deletingResults}
              className={`px-4 py-2 rounded-md text-white ${
                deletingResults
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-red-800 hover:bg-red-900"
              }`}
            >
              {deletingResults ? "Mažu…" : "Smazat turnaje z databáze"}
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Export vytvoří záložní kopii všech dat aplikace. Import přepíše
            všechna existující data. Všechna data jsou také automaticky ukládána
            ve vašem prohlížeči. "Vymazat turnaje" smaže jen rozehranou tabulku
            a lokální archiv turnajů – hráči i nastavení zůstanou. "Smazat
            turnaje z databáze" nevratně smaže celou historii turnajů ve
            Firebase; před smazáním se stáhne záloha do JSON a hráči zůstanou
            nedotčeni.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Configuration;
